import React, { useState, useEffect } from "react";
import { Box, Typography, Fab, Tooltip, Paper, useTheme, Chip, Button, Grid, Card, CardContent } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet";
import DriveEtaIcon from "@mui/icons-material/DriveEta";
import HowToRegIcon from "@mui/icons-material/HowToReg";
import HailIcon from "@mui/icons-material/Hail";
import SettingsIcon from "@mui/icons-material/Settings";

import Drivers from "./Drivers";
import ModuleSettingsModal from "../components/ModuleSettingsModal";

const TABS = [
  { value: "drivers", label: "Registered Drivers", icon: HowToRegIcon },
  { value: "rides", label: "Scheduled Rides", icon: DriveEtaIcon },
  { value: "requests", label: "Passenger Requests", icon: HailIcon },
];

const RideConnect = () => {
  const theme = useTheme();
  const isLight = theme.palette.mode === 'light';
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") || "drivers";
  const [activeTab, setActiveTab] = useState(initialTab);
  const [settingsOpen, setSettingsOpen] = useState(false);

  useEffect(() => {
    const tabParam = searchParams.get("tab");
    if (tabParam && tabParam !== activeTab && ["drivers", "rides", "requests"].includes(tabParam)) {
      setActiveTab(tabParam);
    }
  }, [searchParams, activeTab]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearchParams({ tab });
  };

  const BORDER_COLOR = isLight ? "rgba(43, 130, 144, 0.25)" : "rgba(76, 161, 175, 0.45)";
  const PANEL_BG = isLight ? "#FFFFFF" : "#182232";

  return (
    <Box sx={{ width: "100%", pb: 4, pt: 1 }}>
      <Helmet><title>Ride Connect - GPN</title></Helmet>

      {/* ── Top Bar: Module Title + Tabs in one continuous row ── */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-end",
          position: "relative",
          zIndex: 2,
          mb: "-1.5px",
          px: 1,
        }}
      >
        {/* Module Title — styled as a label, NOT a button */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.2,
            pb: 1,
            px: 1,
            mr: 3.5,
            userSelect: "none",
            cursor: "default",
            borderBottom: isLight ? "2.5px solid rgba(43, 130, 144, 0.65)" : "2.5px solid rgba(76, 161, 175, 0.65)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 32,
              height: 32,
              borderRadius: "8px",
              bgcolor: isLight ? "rgba(43, 130, 144, 0.12)" : "rgba(76, 161, 175, 0.18)",
              border: isLight ? "1.5px solid rgba(43, 130, 144, 0.4)" : "1.5px solid rgba(76, 161, 175, 0.5)",
              color: isLight ? "#2B8290" : "#64d2e2",
              flexShrink: 0,
            }}
          >
            <DriveEtaIcon sx={{ fontSize: 18 }} />
          </Box>
          <Typography
            variant="subtitle1"
            fontWeight={800}
            sx={{
              color: isLight ? "#0F172A" : "#e0f2fe",
              letterSpacing: "0.6px",
              fontSize: "1.25rem",
              lineHeight: 1.15,
              textTransform: "uppercase",
              fontFamily: "'Inter', sans-serif",
            }}
          >
            Ride Connect
          </Typography>
        </Box>

        {/* Vertical Divider separating Title from Tab buttons */}
        <Box
          sx={{
            width: "1.5px",
            height: 24,
            bgcolor: isLight ? "rgba(0, 0, 0, 0.12)" : "rgba(255, 255, 255, 0.15)",
            mr: 2.5,
            mb: 0.8,
          }}
        />

        {/* Tab Items */}
        <Box sx={{ display: "flex", alignItems: "flex-end", gap: 0.5 }}>
          {TABS.map(({ value, label, icon: Icon }) => {
            const isActive = activeTab === value;
            return (
              <Box
                key={value}
                onClick={() => handleTabChange(value)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.8,
                  px: 2.4,
                  py: 1.1,
                  cursor: "pointer",
                  userSelect: "none",
                  fontSize: "0.88rem",
                  fontWeight: isActive ? 600 : 400,
                  color: isActive ? (isLight ? "#2B8290" : "#FFFFFF") : (isLight ? "#64748B" : "rgba(255, 255, 255, 0.65)"),
                  transition: "color 0.2s ease",
                  ...(isActive
                    ? {
                        backgroundColor: PANEL_BG,
                        borderTop: `1.5px solid ${BORDER_COLOR}`,
                        borderLeft: `1.5px solid ${BORDER_COLOR}`,
                        borderRight: `1.5px solid ${BORDER_COLOR}`,
                        borderRadius: "8px 8px 0 0",
                        position: "relative",
                        zIndex: 10,
                        "&::after": {
                          content: '""',
                          position: "absolute",
                          left: 0,
                          right: 0,
                          bottom: "-2px",
                          height: "4px",
                          backgroundColor: PANEL_BG,
                          zIndex: 12,
                        },
                      }
                    : {
                        backgroundColor: "transparent",
                        border: "none",
                        "&:hover": {
                          color: isLight ? "#2B8290" : "#FFFFFF",
                        },
                      }),
                }}
              >
                <Icon sx={{ fontSize: 17, color: isActive ? (isLight ? "#2B8290" : "#4CA1AF") : (isLight ? "#64748B" : "rgba(255, 255, 255, 0.55)") }} />
                <span>{label}</span>
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* ── Main Content Box: Connected directly under the active tab ── */}
      <Box
        sx={{
          width: "100%",
          border: `1.5px solid ${BORDER_COLOR}`,
          borderRadius: "0 10px 10px 10px",
          backgroundColor: PANEL_BG,
          backdropFilter: "blur(20px)",
          boxShadow: isLight ? "0 4px 20px rgba(0, 0, 0, 0.05)" : "0 10px 30px rgba(0, 0, 0, 0.35)",
          p: { xs: 2, sm: 2.8 },
          position: "relative",
          zIndex: 1,
          minHeight: "75vh",
        }}
      >
        {activeTab === "drivers" && <Drivers hideHeader={true} />}

        {activeTab === "rides" && (
          <Box sx={{ py: 6, px: 2, textAlign: "center", maxWidth: 720, mx: "auto" }}>
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: "20px",
                bgcolor: isLight ? "rgba(43, 130, 144, 0.12)" : "rgba(76, 161, 175, 0.2)",
                border: isLight ? "2px solid rgba(43, 130, 144, 0.3)" : "2px solid rgba(76, 161, 175, 0.4)",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 2.5,
              }}
            >
              <DriveEtaIcon sx={{ fontSize: 44, color: isLight ? "#2B8290" : "#4CA1AF" }} />
            </Box>

            <Box sx={{ mb: 1.5 }}>
              <Chip
                label="Feature in Development • Coming Next Sprint"
                size="small"
                sx={{
                  bgcolor: isLight ? "rgba(43, 130, 144, 0.12)" : "rgba(76, 161, 175, 0.25)",
                  color: isLight ? "#2B8290" : "#64d2e2",
                  fontWeight: 700,
                  fontSize: "0.75rem",
                  letterSpacing: 0.5,
                  mb: 1.5,
                  border: isLight ? "1px solid rgba(43, 130, 144, 0.3)" : "1px solid rgba(76, 161, 175, 0.4)",
                }}
              />
              <Typography variant="h5" fontWeight={700} sx={{ color: isLight ? "#0F172A" : "#FFFFFF" }}>
                Scheduled Rides & Route Matching
              </Typography>
            </Box>

            <Typography variant="body2" sx={{ color: isLight ? "#64748B" : "rgba(255, 255, 255, 0.7)", maxWidth: 540, mx: "auto", mb: 4, lineHeight: 1.6 }}>
              This module will allow verified church drivers to post recurring or one-time ride schedules for Sunday services, mid-week fellowships, and special conferences.
            </Typography>

            <Grid container spacing={2} sx={{ mb: 4, textAlign: "left" }}>
              <Grid item xs={12} sm={4}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: isLight ? "#F8FAFC" : "rgba(255, 255, 255, 0.04)",
                    borderColor: isLight ? "#E2E8F0" : "rgba(255, 255, 255, 0.1)",
                    height: "100%",
                  }}
                >
                  <Typography variant="caption" fontWeight={700} sx={{ color: isLight ? "#2B8290" : "#4CA1AF" }}>PHASE 1</Typography>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ mt: 0.5, color: isLight ? "#0F172A" : "#FFFFFF" }}>Driver Rostering</Typography>
                  <Typography variant="caption" sx={{ color: isLight ? "#64748B" : "rgba(255, 255, 255, 0.6)" }}>Drivers onboarded & verified via the Drivers tab.</Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: isLight ? "#F8FAFC" : "rgba(255, 255, 255, 0.04)",
                    borderColor: isLight ? "#E2E8F0" : "rgba(255, 255, 255, 0.1)",
                    height: "100%",
                  }}
                >
                  <Typography variant="caption" fontWeight={700} sx={{ color: isLight ? "#2B8290" : "#4CA1AF" }}>PHASE 2 (UPCOMING)</Typography>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ mt: 0.5, color: isLight ? "#0F172A" : "#FFFFFF" }}>Route Scheduling</Typography>
                  <Typography variant="caption" sx={{ color: isLight ? "#64748B" : "rgba(255, 255, 255, 0.6)" }}>Departure points, seat capacity, and destination routes.</Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: isLight ? "#F8FAFC" : "rgba(255, 255, 255, 0.04)",
                    borderColor: isLight ? "#E2E8F0" : "rgba(255, 255, 255, 0.1)",
                    height: "100%",
                  }}
                >
                  <Typography variant="caption" fontWeight={700} sx={{ color: isLight ? "#2B8290" : "#4CA1AF" }}>PHASE 3</Typography>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ mt: 0.5, color: isLight ? "#0F172A" : "#FFFFFF" }}>Mobile Integration</Typography>
                  <Typography variant="caption" sx={{ color: isLight ? "#64748B" : "rgba(255, 255, 255, 0.6)" }}>Congregants book rides directly in the mobile app.</Typography>
                </Paper>
              </Grid>
            </Grid>

            <Button
              variant="outlined"
              onClick={() => handleTabChange("drivers")}
              sx={{
                color: isLight ? "#2B8290" : "#64d2e2",
                borderColor: isLight ? "#2B8290" : "rgba(76, 161, 175, 0.5)",
                textTransform: "none",
                fontWeight: 600,
                borderRadius: 2,
                px: 3,
                '&:hover': {
                  borderColor: isLight ? "#1B5E6B" : "#4CA1AF",
                  bgcolor: isLight ? "rgba(43, 130, 144, 0.08)" : "rgba(76, 161, 175, 0.1)",
                }
              }}
            >
              View Registered Drivers
            </Button>
          </Box>
        )}

        {activeTab === "requests" && (
          <Box sx={{ py: 6, px: 2, textAlign: "center", maxWidth: 720, mx: "auto" }}>
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: "20px",
                bgcolor: isLight ? "rgba(43, 130, 144, 0.12)" : "rgba(76, 161, 175, 0.2)",
                border: isLight ? "2px solid rgba(43, 130, 144, 0.3)" : "2px solid rgba(76, 161, 175, 0.4)",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 2.5,
              }}
            >
              <HailIcon sx={{ fontSize: 44, color: isLight ? "#2B8290" : "#4CA1AF" }} />
            </Box>

            <Box sx={{ mb: 1.5 }}>
              <Chip
                label="Feature in Development • Coming Next Sprint"
                size="small"
                sx={{
                  bgcolor: isLight ? "rgba(43, 130, 144, 0.12)" : "rgba(76, 161, 175, 0.25)",
                  color: isLight ? "#2B8290" : "#64d2e2",
                  fontWeight: 700,
                  fontSize: "0.75rem",
                  letterSpacing: 0.5,
                  mb: 1.5,
                  border: isLight ? "1px solid rgba(43, 130, 144, 0.3)" : "1px solid rgba(76, 161, 175, 0.4)",
                }}
              />
              <Typography variant="h5" fontWeight={700} sx={{ color: isLight ? "#0F172A" : "#FFFFFF" }}>
                Passenger Pickup & Drop Requests
              </Typography>
            </Box>

            <Typography variant="body2" sx={{ color: isLight ? "#64748B" : "rgba(255, 255, 255, 0.7)", maxWidth: 540, mx: "auto", mb: 4, lineHeight: 1.6 }}>
              Review ride requests submitted by community members and seniors needing transportation assistance to Sunday services and church gatherings.
            </Typography>

            <Grid container spacing={2} sx={{ mb: 4, textAlign: "left" }}>
              <Grid item xs={12} sm={4}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: isLight ? "#F8FAFC" : "rgba(255, 255, 255, 0.04)",
                    borderColor: isLight ? "#E2E8F0" : "rgba(255, 255, 255, 0.1)",
                    height: "100%",
                  }}
                >
                  <Typography variant="caption" fontWeight={700} sx={{ color: isLight ? "#2B8290" : "#4CA1AF" }}>REQUEST QUEUE</Typography>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ mt: 0.5, color: isLight ? "#0F172A" : "#FFFFFF" }}>Member Requests</Typography>
                  <Typography variant="caption" sx={{ color: isLight ? "#64748B" : "rgba(255, 255, 255, 0.6)" }}>Incoming pickup requests from congregants.</Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: isLight ? "#F8FAFC" : "rgba(255, 255, 255, 0.04)",
                    borderColor: isLight ? "#E2E8F0" : "rgba(255, 255, 255, 0.1)",
                    height: "100%",
                  }}
                >
                  <Typography variant="caption" fontWeight={700} sx={{ color: isLight ? "#2B8290" : "#4CA1AF" }}>DISPATCH</Typography>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ mt: 0.5, color: isLight ? "#0F172A" : "#FFFFFF" }}>Driver Assignment</Typography>
                  <Typography variant="caption" sx={{ color: isLight ? "#64748B" : "rgba(255, 255, 255, 0.6)" }}>Assign verified volunteer drivers to open requests.</Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: isLight ? "#F8FAFC" : "rgba(255, 255, 255, 0.04)",
                    borderColor: isLight ? "#E2E8F0" : "rgba(255, 255, 255, 0.1)",
                    height: "100%",
                  }}
                >
                  <Typography variant="caption" fontWeight={700} sx={{ color: isLight ? "#2B8290" : "#4CA1AF" }}>NOTIFICATIONS</Typography>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ mt: 0.5, color: isLight ? "#0F172A" : "#FFFFFF" }}>Status Updates</Typography>
                  <Typography variant="caption" sx={{ color: isLight ? "#64748B" : "rgba(255, 255, 255, 0.6)" }}>Push alerts when a driver accepts or completes a ride.</Typography>
                </Paper>
              </Grid>
            </Grid>

            <Button
              variant="outlined"
              onClick={() => handleTabChange("drivers")}
              sx={{
                color: isLight ? "#2B8290" : "#64d2e2",
                borderColor: isLight ? "#2B8290" : "rgba(76, 161, 175, 0.5)",
                textTransform: "none",
                fontWeight: 600,
                borderRadius: 2,
                px: 3,
                '&:hover': {
                  borderColor: isLight ? "#1B5E6B" : "#4CA1AF",
                  bgcolor: isLight ? "rgba(43, 130, 144, 0.08)" : "rgba(76, 161, 175, 0.1)",
                }
              }}
            >
              View Registered Drivers
            </Button>
          </Box>
        )}
      </Box>

      {/* ── Settings icon at bottom-right corner ── */}
      <Tooltip title="Drivers Settings" placement="left">
        <Fab
          size="medium"
          onClick={() => setSettingsOpen(true)}
          sx={{
            position: "fixed",
            bottom: 28,
            right: 28,
            bgcolor: isLight ? "#2B8290" : "#4CA1AF",
            color: "#FFFFFF",
            boxShadow: isLight ? "0 6px 20px rgba(43, 130, 144, 0.3)" : "0 6px 20px rgba(0, 0, 0, 0.4)",
            "&:hover": {
              bgcolor: isLight ? "#1B5E6B" : "#3A8A97",
              transform: "scale(1.05)",
            },
            transition: "all 0.2s ease-in-out",
            zIndex: 1100,
          }}
        >
          <SettingsIcon />
        </Fab>
      </Tooltip>

      <ModuleSettingsModal
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        moduleKey="driver"
        moduleTitle="Ride Connect"
      />
    </Box>
  );
};

export default RideConnect;
